import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Note component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconNote extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M2.84196 2.84269C2.99401 2.69064 3.20023 2.60522 3.41526 2.60522H20.5837C20.7988 2.60522 21.005 2.69064 21.157 2.84269C21.3091 2.99474 21.3945 3.20096 21.3945 3.41599V13.5916H15.121C14.7151 13.5916 14.3258 13.7528 14.0388 14.0398C13.7518 14.3268 13.5906 14.7161 13.5906 15.1219V21.3952H3.41526C3.20023 21.3952 2.99401 21.3098 2.84196 21.1578C2.68991 21.0057 2.60449 20.7995 2.60449 20.5845V3.41599C2.60449 3.20096 2.68991 2.99474 2.84196 2.84269ZM20.3337 15.0916L15.0906 20.3346V15.1219C15.0906 15.1139 15.0938 15.1061 15.0995 15.1005C15.1052 15.0948 15.1129 15.0916 15.121 15.0916H20.3337ZM14.3234 22.8952H3.41526C2.80241 22.8952 2.21465 22.6518 1.7813 22.2184C1.34795 21.7851 1.10449 21.1973 1.10449 20.5845V3.41599C1.10449 2.80314 1.34795 2.21539 1.7813 1.78203C2.21465 1.34868 2.80241 1.10522 3.41526 1.10522H20.5837C21.1966 1.10522 21.7843 1.34868 22.2177 1.78203C22.651 2.21539 22.8945 2.80314 22.8945 3.41599V14.3414C22.8945 14.4254 22.8804 14.508 22.8535 14.5859C22.8171 14.6915 22.757 14.7896 22.6748 14.8719L14.8709 22.6757C14.7233 22.8234 14.5244 22.8998 14.3234 22.8952Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-note') === undefined) {
  customElements.define('cat-icon-note', BlocksIconNote);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-note': BlocksIconNote;
  }
}
